import { computed, makeAutoObservable } from 'mobx';
import { Common } from 'src/AppState/Common';
import { AutoMap } from 'src_common/common/mobx-utils/AutoMap';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { MarketType, PER_PAGE, serializeNodeValue, TreeType } from './utils';
import {
    apiGetMarketsRequest,
    ApiGetMarketsResponse200Type,
    ApiGetMarketsResponseType,
} from 'src/api_openapi/v2/trading-search/apiGetMarkets';

export class MarketStoreState {
    private readonly allMarketsResource: AutoMap<number, Resource<ApiGetMarketsResponseType | null>>;

    public constructor(
        private readonly common: Common,
        private readonly sportId: string,
        private readonly competitionId: string,
        private readonly eventId: string,
        private readonly readOnlyMode: boolean
    ) {
        this.allMarketsResource = new AutoMap((page) => {
            return new Resource(() => this.fetchMarkets(page));
        });

        makeAutoObservable(this);
    }

    private async fetchMarkets(page: number): Promise<ApiGetMarketsResponseType> {
        const getEventsReponse = await apiGetMarketsRequest(
            `${this.common.appEnvParams.gatewayUrl}`,
            {
                sportId: this.sportId,
                competitionId: this.competitionId,
                eventId: this.eventId,
                page,
                pageSize: PER_PAGE,
                active: true,
            },
            {
                authorization: `Bearer ${this.common.session.currentJwt}`,
            }
        );
        return getEventsReponse;
    }

    private getMarketsPage = (page: number): ApiGetMarketsResponse200Type | 'loading' | 'error' => {
        const res = this.allMarketsResource.get(page).get();

        if (res.type === 'loading') {
            return 'loading';
        }

        if (res.type === 'error' || res.value === null || res.value.status !== 200) {
            return 'error';
        }

        return res.value.body;
    };

    @computed public get getMarkets(): TreeType[] | 'loading' | 'error' {
        const fetchedItems: Array<MarketType> = [];
        const resFirstPage = this.getMarketsPage(1);

        if (resFirstPage === 'loading') {
            return 'loading';
        }

        if (resFirstPage === 'error') {
            return 'error';
        }

        resFirstPage.items.map((item) => {
            fetchedItems.push(item);
        });

        // load other pages
        const pageCount = resFirstPage.pagination.pageCount;
        if (pageCount > 1) {
            for (let currentPage = 2; currentPage <= pageCount; currentPage++) {
                const resAnotherPage = this.getMarketsPage(currentPage);

                if (resAnotherPage === 'loading') {
                    return 'loading';
                }

                if (resAnotherPage === 'error') {
                    return 'error';
                }

                resAnotherPage.items.map((item) => {
                    fetchedItems.push(item);
                });
            }
        }

        return fetchedItems.map((market) => {
            const serializedValue = serializeNodeValue({
                type: 'market',
                sportId: this.sportId,
                competitionId: this.competitionId,
                eventId: this.eventId,
                marketId: market.marketId,
            });
            return {
                id: serializedValue,
                value: serializedValue,
                label: market.name,
                showCheckbox: !this.readOnlyMode,
            };
        });
    }

    @computed public get isLoading(): boolean {
        if (this.getMarkets === 'loading') {
            return true;
        }
        return false;
    }
}
